import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useLang, RESERVE_URL } from "../context/LanguageContext";

export default function Pricing() {
  const { t } = useLang();
  const p = t.pricing;
  const founder = p.founder;

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative bg-[#050505] py-24 md:py-36 border-t border-white/5 overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-10 right-0 font-display text-[24rem] md:text-[32rem] text-white/[0.025] leading-none select-none hidden md:block">
        49,90
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16">
          <div className="text-xs tracking-[0.3em] uppercase font-bold text-[#FFCC00] mb-6">
            / 04 — {p.overline}
          </div>
          <h2
            data-testid="pricing-title"
            className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[0.92] mb-6"
          >
            {p.title}
          </h2>
          <p className="text-base md:text-lg text-white/70 font-body">{p.desc}</p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 items-stretch">
          {/* LEFT — Founder */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            data-testid="founder-card"
            className="md:col-span-7 relative overflow-hidden group"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#FFCC00] via-[#FFD633] to-[#E6B800]" />
            <div className="absolute inset-[1.5px] bg-gradient-to-br from-[#FFCC00] to-[#f5b800] text-black" />
            <div className="absolute -right-16 -top-16 w-72 h-72 border-[3px] border-black/10 rotate-12 pointer-events-none" />

            <div className="relative p-8 md:p-12 lg:p-14 text-black">
              <div
                data-testid="founder-label"
                className="inline-flex items-center gap-2 bg-black text-[#FFCC00] px-4 py-2 mb-6 text-[11px] md:text-[12px] tracking-[0.18em] uppercase font-bold"
              >
                {founder.plan_name}
              </div>

              {/* Matrícula gratuita — oferta destacada */}
              <div
                data-testid="founder-enrollment"
                className="flex items-center gap-3 mb-8"
              >
                <div className="bg-black text-[#FFCC00] px-5 py-3">
                  <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#FFCC00]/70 mb-0.5">Oferta</p>
                  <p className="font-display text-2xl md:text-3xl leading-none">{founder.enrollment_label}</p>
                  <p className="text-[11px] text-[#FFCC00]/60 font-bold mt-1">
                    (Valor{" "}
                    <span className="relative inline-block">
                      {founder.enrollment_old}
                      <span className="absolute left-[-6%] right-[-6%] top-1/2 h-[1.5px] bg-red-400 -rotate-6" />
                    </span>
                    )
                  </p>
                </div>
              </div>

              <div className="flex items-baseline gap-3 mb-2">
                <span
                  data-testid="founder-price"
                  className="font-display text-7xl md:text-8xl lg:text-9xl leading-none"
                  style={{ textShadow: "0 2px 0 rgba(0,0,0,0.15)" }}
                >
                  {founder.price}
                </span>
              </div>
              <div className="text-sm md:text-base font-bold uppercase tracking-widest mb-6">
                {founder.period}
              </div>

              <div className="h-px w-24 bg-black/30 my-6 md:my-8" />

              <ul className="space-y-3 mb-10">
                {founder.features.map((f, i) => (
                  <li
                    key={i}
                    data-testid={`founder-feature-${i}`}
                    className="flex items-start gap-3 text-[15px] md:text-base font-semibold"
                  >
                    <Check size={18} className="mt-0.5 shrink-0" strokeWidth={3} />
                    {f}
                  </li>
                ))}
              </ul>

              <a
                href={RESERVE_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founder-cta"
                className="inline-flex items-center justify-center gap-3 w-full md:w-auto px-10 py-5 bg-black text-[#FFCC00] font-bold text-xs md:text-sm tracking-[0.22em] uppercase hover:bg-white hover:text-black transition-colors"
              >
                {founder.cta}
                <span className="inline-block w-6 h-px bg-current" />
              </a>

              {founder.note && (
                <p data-testid="founder-note" className="mt-4 text-xs md:text-[13px] text-black/60 font-medium">
                  {founder.note}
                </p>
              )}
            </div>
          </motion.div>

          {/* RIGHT — Standard */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="standard-card"
            className="md:col-span-5 bg-[#0a0a0a] border border-white/10 p-8 md:p-10 flex flex-col"
          >
            <div data-testid="standard-label" className="text-[10px] md:text-[11px] tracking-[0.28em] uppercase font-bold text-white/60 mb-6">
              {p.standard.label}
            </div>

            <div className="flex items-center gap-2 mb-4">
              <span className="w-1.5 h-1.5 bg-white/40 rotate-45" />
              <span className="text-[11px] tracking-[0.28em] font-bold uppercase text-white/80">
                {p.standard.plan_name}
              </span>
            </div>

            <div className="flex items-baseline gap-2 mb-8">
              <span data-testid="standard-price" className="font-display text-5xl md:text-6xl text-white leading-none">
                {p.standard.price}
              </span>
              <span className="text-xs font-bold uppercase tracking-widest text-white/60">
                {p.standard.period}
              </span>
            </div>

            <div className="h-px w-16 bg-white/15 mb-8" />

            <ul className="space-y-3 mb-10 flex-1">
              {p.standard.features.map((f, i) => (
                <li key={i} data-testid={`standard-feature-${i}`} className="flex items-start gap-3 text-sm text-white/80">
                  <Check size={16} className="mt-0.5 shrink-0 text-[#FFCC00]" strokeWidth={2.5} />
                  {f}
                </li>
              ))}
            </ul>

            <a
              href={RESERVE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="standard-cta"
              className="inline-flex items-center justify-center gap-3 w-full px-6 py-4 border border-white/25 text-white font-bold text-[11px] md:text-xs tracking-[0.22em] uppercase hover:border-[#FFCC00] hover:text-[#FFCC00] transition-colors"
            >
              {p.standard.cta}
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
